import { useCallback, useMemo } from 'react';

import { useTimezone } from './TimezoneProvider';

/**
 * Timestamp formatting, always in the active display zone.
 *
 * Nothing in the app should call `toLocaleString` directly. Every formatter here takes the
 * zone from context, so changing the preference reformats the whole app rather than leaving
 * whichever components happened to inline a conversion showing the browser's zone.
 *
 * `en-AU` is pinned rather than taken from the browser: a case id and a due date get read
 * aloud on an incident call, and `11/09/2026` meaning two different dates depending on whose
 * laptop is displaying it is a genuine hazard in that setting. Day-month order, always.
 */
const LOCALE = 'en-AU';

/** API timestamps are UTC ISO 8601. Returns null rather than an Invalid Date. */
export function parseInstant(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export interface Formatters {
  /** The zone in use, for labelling. */
  zone: string;
  /** `11 Sept 2026, 4:05 pm` */
  dateTime: (value: string | null | undefined) => string;
  /** `11 Sept 2026` */
  date: (value: string | null | undefined) => string;
  /** `4:05 pm` */
  time: (value: string | null | undefined) => string;
  /** `11 Sept 2026, 16:05:22 AEST` — for the audit trail, where precision matters. */
  precise: (value: string | null | undefined) => string;
  /** `3 days ago`, `in 7 hours`. Zone-independent, so it needs no formatter. */
  relative: (value: string | null | undefined) => string;
  /** Whole days until the value; negative when it has passed. Null when unparseable. */
  daysUntil: (value: string | null | undefined) => number | null;
}

const EM_DASH = '—';

export function useFormatters(): Formatters {
  const { zone } = useTimezone();

  const dateTimeFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(LOCALE, {
        timeZone: zone,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    [zone],
  );

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(LOCALE, {
        timeZone: zone,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    [zone],
  );

  const timeFormat = useMemo(
    () => new Intl.DateTimeFormat(LOCALE, { timeZone: zone, hour: 'numeric', minute: '2-digit' }),
    [zone],
  );

  const preciseFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(LOCALE, {
        timeZone: zone,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'short',
      }),
    [zone],
  );

  const relativeFormat = useMemo(
    () => new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' }),
    [],
  );

  const wrap = useCallback(
    (formatter: Intl.DateTimeFormat) => (value: string | null | undefined) => {
      const parsed = parseInstant(value);
      return parsed ? formatter.format(parsed) : EM_DASH;
    },
    [],
  );

  const relative = useCallback(
    (value: string | null | undefined) => {
      const parsed = parseInstant(value);
      if (!parsed) return EM_DASH;
      const seconds = (parsed.getTime() - Date.now()) / 1000;
      const steps: [Intl.RelativeTimeFormatUnit, number][] = [
        ['second', 60],
        ['minute', 60],
        ['hour', 24],
        ['day', 7],
        ['week', 4.35],
        ['month', 12],
      ];
      let amount = seconds;
      for (const [unit, size] of steps) {
        if (Math.abs(amount) < size) return relativeFormat.format(Math.round(amount), unit);
        amount /= size;
      }
      return relativeFormat.format(Math.round(amount), 'year');
    },
    [relativeFormat],
  );

  /**
   * Counted in *calendar days in the display zone*, not in 24-hour blocks.
   *
   * "Overdue 3d" has to agree with what a reader sees on a calendar, and dividing elapsed
   * milliseconds by 86 400 000 disagrees with it around midnight and across a DST boundary.
   * Both endpoints are reduced to a civil date in the active zone first.
   */
  const civilFormat = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    [zone],
  );

  const daysUntil = useCallback(
    (value: string | null | undefined) => {
      const parsed = parseInstant(value);
      if (!parsed) return null;
      // Read the parts rather than interpolating the formatted string: the assembled order
      // and separators are locale- and ICU-dependent, and a format that came out as
      // `11/09/2026` instead of `2026-09-11` would parse as a different date rather than
      // failing, which is the worst way for this to be wrong.
      const civil = (instant: Date): number => {
        const parts = civilFormat.formatToParts(instant);
        const part = (type: Intl.DateTimeFormatPartTypes) =>
          Number(parts.find((p) => p.type === type)?.value);
        return Date.UTC(part('year'), part('month') - 1, part('day'));
      };
      return Math.round((civil(parsed) - civil(new Date())) / 86_400_000);
    },
    [civilFormat],
  );

  return useMemo(
    () => ({
      zone,
      dateTime: wrap(dateTimeFormat),
      date: wrap(dateFormat),
      time: wrap(timeFormat),
      precise: wrap(preciseFormat),
      relative,
      daysUntil,
    }),
    [zone, wrap, dateTimeFormat, dateFormat, timeFormat, preciseFormat, relative, daysUntil],
  );
}
